import React, { useState, useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import { Form, Input, message, Modal, Space, Typography } from 'antd-v4'
import { CurrencyInput } from '../../../../components/CurrencyInput'
import TextArea from 'antd-v4/lib/input/TextArea'
import { Button } from 'antd'
import { ILocationZoneContract, LOCATION_ZONE_OPTION } from '@getgreenline/blaze-ecomm'
import { isEmpty } from 'lodash'
import { BlazeButton } from '../../../../components/CustomButtons/BlazeButton'
import {
  isValidPostalCodeCA,
  sanitizeLocationNameInput,
  sanitizePostalCodesInput,
} from '../../../../utilities/postalCodes'
import { GrRegex } from '../../../../utilities/helpers'
import { COLORS } from '../../../../constants/Colors'
import { ConfigureEcomDelivery } from './ConfigureEcomDelivery'
import { AWSLocationMap } from './AWSLocationMap'
import KMLUploader from './KMLUploader'

interface IProps {
  locationZone: ILocationZoneContract
  index: number
  configureEcomDelivery: ConfigureEcomDelivery
}

const _DeliveryZoneInputGroup = ({ locationZone, index, configureEcomDelivery }: IProps) => {
  const {
    allLocationZones,
    setAllLocationZones,
    setActiveKeys,
    isPostalCodeOptionEnabled,
    isKMLOptionEnabled,
    uploadKMLZone,
  } = configureEcomDelivery

  const { coordinates, fillColor, lineColor } = locationZone

  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false)

  const [form] = Form.useForm()
  const [locationZoneName, setLocationZoneName] = useState<string>(locationZone.name || '')
  const [isLocationZoneNameOnBlur, setIsLocationZoneNameOnBlur] = useState<boolean>(false)
  const [fee, setFee] = useState<number>(locationZone.fee || 0)
  const [postalCodes, setPostalCodes] = useState<string>(`${locationZone.postalCodes}` || '')
  const [invalidPostalCodes, setInvalidPostalCodes] = useState<string[]>([])
  const [invalidPostalCodesMessage, setInvalidPostalCodesMessage] = useState<string>()

  const newLocationZoneObject: ILocationZoneContract = {
    ...locationZone,
    name: locationZoneName,
    fee: fee,
    postalCodes:
      locationZone.locationZoneOption !== LOCATION_ZONE_OPTION.KML ? postalCodes.split(',') : [],
  }

  const updateLocationZone = (index: number, updatedLocationZone: ILocationZoneContract) => {
    setShowDeleteModal(false)
    const updatedLocationZones = [...allLocationZones]
    updatedLocationZones[index] = updatedLocationZone
    setAllLocationZones(updatedLocationZones)
  }

  const removeOneLocationZone = () => {
    setShowDeleteModal(false)
    removeLocationZone()
  }

  const removeLocationZone = () => {
    const updatedLocationZones = [...allLocationZones]
    updatedLocationZones.splice(index, 1)
    setAllLocationZones(updatedLocationZones)
    setActiveKeys([])
  }

  const getInvalidPostalCodes = () => {
    const invalidPostalCodes = postalCodes
      .split(',')
      .filter((postalCode: string) => !isValidPostalCodeCA(postalCode.trim()))
      .map((invalidPostalCode: string) => `'${invalidPostalCode.trim() || ' '}'`)
    setInvalidPostalCodes(invalidPostalCodes)
    if (invalidPostalCodes.length > 0) {
      const errorMessage =
        invalidPostalCodes.length > 1
          ? `[${invalidPostalCodes.join(', ')}] are not valid postal codes.`
          : `${invalidPostalCodes[0]} is not a valid postal code.`
      setInvalidPostalCodesMessage(errorMessage)
    } else {
      setInvalidPostalCodesMessage(undefined)
    }
  }

  useEffect(() => {
    updateLocationZone(index, newLocationZoneObject)
    getInvalidPostalCodes()
  }, [locationZoneName, fee, postalCodes])

  useEffect(() => {
    setLocationZoneName(locationZone.name || '')
    setFee(locationZone.fee || 0)
    setPostalCodes(`${locationZone.postalCodes}` || '')
  }, [locationZone])

  return (
    <>
      <Form form={form} name='local-zone-input'>
        <Form.Item
          label='Zone'
          name='locationZoneName'
          id='locationZoneName'
          help={!locationZoneName && isLocationZoneNameOnBlur ? 'Please enter your zone name' : ' '}
          rules={[
            { required: true },
            { pattern: GrRegex.excludeFlatRate, message: 'Invalid zone name' },
          ]}
          initialValue={locationZoneName}
        >
          <Space className='delivery-zone-input-group' size='middle'>
            <Typography.Paragraph className='delivery-zone-text-description' type='secondary'>
              The name of your zone
            </Typography.Paragraph>
            <Input
              className='delivery-zone-input'
              id='locationZoneName'
              name='locationZoneName'
              value={locationZoneName}
              style={{
                borderColor:
                  !locationZoneName && isLocationZoneNameOnBlur
                    ? COLORS.dangerBorder
                    : COLORS.borderGrey,
              }}
              maxLength={255}
              onChange={({ target }) =>
                setLocationZoneName(sanitizeLocationNameInput(target.value))
              }
              onBlur={() => setIsLocationZoneNameOnBlur(true)}
              onFocus={() => setIsLocationZoneNameOnBlur(false)}
            />
          </Space>
        </Form.Item>
        <Form.Item label='Fee' name='fee' initialValue={fee}>
          <Space size='middle' className='delivery-zone-input-group'>
            <Typography.Paragraph className='delivery-zone-text-description' type='secondary'>
              The price of your delivery fee
            </Typography.Paragraph>
            <CurrencyInput
              name='fee'
              className='form-control delivery-zone-input'
              centValue={fee}
              onChange={setFee}
              onlyPositiveNumber
            />
          </Space>
        </Form.Item>

        {isPostalCodeOptionEnabled && (
          <Form.Item
            label='Postal Code'
            name='postalCode'
            initialValue={postalCodes}
            validateStatus={!isEmpty(invalidPostalCodes) ? 'error' : ''}
            help={
              <span style={{ display: 'inline-block' }}>
                {!isEmpty(invalidPostalCodes) && postalCodes !== ''
                  ? invalidPostalCodesMessage
                  : ' '}
              </span>
            }
            rules={[{ required: true, message: 'Please enter at least one postal code' }]}
            validateFirst
          >
            <Space size='middle' className='delivery-zone-input-group'>
              <Typography.Paragraph className='delivery-zone-text-description' type='secondary'>
                Areas you deliver to. <br />
                Use commas to separate the values. <br />
                You can use * as a wildcard. If you add a *, the system will accept any postal code
                that matches the characters entered before the *, e.g.: R2* will accept R2P3E1,
                R2P4E1, R2P5E1, and so on. <br />
                If you add only the * it will accept all postal codes in Canada.
              </Typography.Paragraph>
              <TextArea
                name='postalCodes'
                placeholder={'R1C*, R2*, P1C*'}
                maxLength={250}
                className='delivery-zone-input'
                autoSize={{ minRows: 8, maxRows: 8 }}
                value={postalCodes}
                onChange={({ target }) => setPostalCodes(sanitizePostalCodesInput(target.value))}
              ></TextArea>
            </Space>
          </Form.Item>
        )}

        {isKMLOptionEnabled && (
          <Form.Item
            label='KML'
            name='kml'
            initialValue={locationZone.coordinates}
            rules={[{ required: true, message: 'Please upload a KML file' }]}
            validateFirst
          >
            <Space size='middle' className='delivery-zone-input-group'>
              <Typography.Paragraph className='delivery-zone-text-description' type='secondary'>
                <KMLUploader
                  upload={(data) => {
                    if (data.length !== 1) {
                      return message.error(
                        <div>
                          Please upload a single layer KML file. <br />
                          Found: {data.length} zones.
                        </div>,
                      )
                    }

                    const polygonData = data[0]
                    uploadKMLZone(polygonData, locationZone)
                  }}
                />
              </Typography.Paragraph>

              {coordinates && lineColor && fillColor && (
                <AWSLocationMap
                  data={{
                    coordinates,
                    lineColor,
                    fillColor,
                  }}
                />
              )}
            </Space>
          </Form.Item>
        )}

        <Form.Item>
          <Form.Item style={{ display: 'flex', justifyContent: 'end' }}>
            <Button icon='delete' type={'danger'} ghost onClick={() => setShowDeleteModal(true)}>
              Remove zone
            </Button>
          </Form.Item>
          <Modal
            visible={showDeleteModal}
            okText='Delete'
            onCancel={() => setShowDeleteModal(false)}
            title={<p className='font-weight-bold mb-0'>Delete zone "{locationZone.name}"</p>}
            className='text-danger'
            zIndex={1003}
            footer={[
              <BlazeButton
                key='cancel'
                buttonProps={{
                  className: 'mb-2 ml-2',
                  onClick: () => setShowDeleteModal(false),
                }}
                titleProps={{ title: 'Cancel' }}
              />,
              <BlazeButton
                key='delete'
                buttonProps={{
                  className: 'mb-2 ml-2',
                  type: 'danger',
                  onClick: removeOneLocationZone,
                }}
                titleProps={{ title: 'Delete' }}
              />,
            ]}
          >
            Are you sure you want to delete zone "{locationZone.name}"?
          </Modal>
        </Form.Item>
      </Form>
    </>
  )
}

export const DeliveryZoneInputGroup = observer(_DeliveryZoneInputGroup)
